// src/pages/GroupsPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { request } from '../utils/request';
import ReusableHeader from '../components/Header/ReusableHeader';

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [urls, setUrls] = useState([]); // this now contains category docs with category_Name

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    users: [],
    department: '',
    location: '',
    description: '',
    enabled_policies: [],
    enabled_urls: [], // will hold category _id(s)
  });

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success'); // 'success' | 'error' | 'info' | 'warning'

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [grpData, usrData, polData, urlData] = await Promise.all([
        request('groups', { method: 'get' }),
        request('user', { method: 'get' }),
        request('policies', { method: 'get' }),
        request('urls', { method: 'get' }), // returns category docs: { _id, category_Name, Urls, ... }
      ]);
      setGroups(Array.isArray(grpData) ? grpData : []);
      setUsers(Array.isArray(usrData) ? usrData : []);
      setPolicies(Array.isArray(polData) ? polData : []);
      setUrls(Array.isArray(urlData) ? urlData : []);
    } catch (err) {
      console.error('Failed to fetch data for GroupsPage:', err);
      setGroups([]);
      setUsers([]);
      setPolicies([]);
      setUrls([]);
      showSnack('Failed to load data.', 'error');
    }
  };

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const getNames = (ids, list, key = 'username') =>
    ids?.length
      ? ids
          .map((id) => list.find((item) => item._id === id)?.[key] || '—')
          .join(', ')
      : '—';

  const handleOpen = (grp) => {
    if (grp) {
      setFormData({
        id: grp._id,
        name: grp.name || '',
        users: grp.users || [],
        department: grp.department || '',
        location: grp.location || '',
        description: grp.description || '',
        enabled_policies: grp.enabled_policies || [],
        enabled_urls: grp.enabled_urls || [], // category _ids
      });
      setIsEditing(true);
    } else {
      setFormData({
        id: null,
        name: '',
        users: [],
        department: '',
        location: '',
        description: '',
        enabled_policies: [],
        enabled_urls: [],
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name,
        users: formData.users,
        department: formData.department,
        location: formData.location,
        description: formData.description,
        enabled_policies: formData.enabled_policies,
        enabled_urls: formData.enabled_urls, // send category ids
      };
      const opts = isEditing
        ? { method: 'put', params: formData.id, body: payload }
        : { method: 'post', body: payload };
      await request('groups', opts);
      await fetchAll();
      handleClose();
      showSnack(isEditing ? 'Group updated successfully.' : 'Group created successfully.', 'success');
    } catch (err) {
      console.error('Error saving group:', err);
      showSnack('Failed to save group.', 'error');
    }
  };

  // Delete handlers
  const handleDeleteClick = (group) => {
    setDeletingGroup(group);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingGroup(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingGroup || !deletingGroup._id) return;
    setDeleting(true);
    try {
      await request('groups', { method: 'delete', params: deletingGroup._id });
      // Refresh list
      await fetchAll();
      showSnack(`Group "${deletingGroup.name}" successfully deleted.`, 'success');
      handleCancelDelete();
    } catch (err) {
      console.error('Failed to delete group:', err);
      showSnack('Failed to delete group.', 'error');
      setDeleting(false);
    }
  };

  /**
   * Compute available users to show in the Users multi-select.
   *
   * Rule:
   *  - A user may belong to only one group.
   *  - When creating a group: hide users who are already assigned to any group.
   *  - When editing a group: hide users who are assigned to other groups, but keep users already in the current group selectable.
   */
  const computeAvailableUsers = () => {
    // Build a set of user IDs that are assigned to groups OTHER than the one we're editing (or all groups when creating)
    const assignedUserIds = new Set();
    groups.forEach((g) => {
      // if editing and this is the same group, skip it (we want those users to remain selectable)
      if (formData.id && g._id === formData.id) return;
      if (Array.isArray(g.users)) {
        g.users.forEach((uid) => {
          if (uid) assignedUserIds.add(uid.toString());
        });
      }
    });

    // available = users not present in assignedUserIds
    return users.filter((u) => !assignedUserIds.has((u._id || '').toString()));
  };

  const availableUsers = computeAvailableUsers();

  return (
    <Box p={2}>
      {/* Header */}
      <ReusableHeader
        title="Groups"
        breadcrumbs={[{ label: "Groups" }]}
        actionButton={
          <Button
            size="small"
            variant="contained"
            sx={{ backgroundColor: '#2d9cdb', '&:hover': { backgroundColor: '#0b1d51' } }}
            onClick={() => handleOpen()}
          >
            Add Group
          </Button>
        }
      />

      {/* Groups Table */}
      <Paper elevation={2} sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f0f7f5' }}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Policies</TableCell>
                <TableCell>URL Category</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g, i) => (
                <TableRow key={g._id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>{getNames(g.users, users)}</TableCell>
                  <TableCell>{g.department || '—'}</TableCell>
                  <TableCell>{g.location || '—'}</TableCell>
                  <TableCell>{g.description || '—'}</TableCell>
                  <TableCell>{getNames(g.enabled_policies, policies, 'name')}</TableCell>
                  {/* changed to show category_Name instead of parentDomain */}
                  <TableCell>{getNames(g.enabled_urls, urls, 'category_Name')}</TableCell>
              <TableCell align="center">
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
    <IconButton
      size="small"
      sx={{ color: '#2d9cdb' }}
      onClick={() => handleOpen(g)}
      aria-label={`edit-group-${g._id}`}
    >
      <EditIcon fontSize="inherit" />
    </IconButton>

    <IconButton
      size="small"
      sx={{ color: '#e53935' }}
      onClick={() => handleDeleteClick(g)}
      aria-label={`delete-group-${g._id}`}
    >
      <DeleteIcon fontSize="inherit" />
    </IconButton>
  </Box>
</TableCell>
                </TableRow>
              ))}

              {groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No groups found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Group Form Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: '#2d9cdb', color: '#fff', py: 1 }}>
          {isEditing ? 'Edit Group' : 'Add Group'}
        </DialogTitle>
        <DialogContent sx={{ p: 2, mt: '10px' }}>
          <Stack spacing={1}>
            <TextField
              size="small"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
            />

            {/* Users Multi-Select - only show availableUsers */}
            <FormControl size="small" fullWidth>
              <InputLabel>Users</InputLabel>
              <Select
                multiple
                name="users"
                value={formData.users}
                onChange={handleChange}
                input={<OutlinedInput label="Users" />}
                renderValue={(selected) => getNames(selected, users)}
              >
                {availableUsers.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    <Checkbox checked={formData.users.includes(u._id)} />
                    <ListItemText primary={u.username} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                size="small"
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <TextField
              size="small"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />

            {/* Policies Multi-Select */}
            <FormControl size="small" fullWidth>
              <InputLabel>Policies</InputLabel>
              <Select
                multiple
                name="enabled_policies"
                value={formData.enabled_policies}
                onChange={handleChange}
                input={<OutlinedInput label="Policies" />}
                renderValue={(selected) => getNames(selected, policies, 'name')}
              >
                {policies.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    <Checkbox checked={formData.enabled_policies.includes(p._id)} />
                    <ListItemText primary={p.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* URLs Multi-Select (now shows categories by category_Name) */}
            <FormControl size="small" fullWidth>
              <InputLabel>URLs</InputLabel>
              <Select
                multiple
                name="enabled_urls"
                value={formData.enabled_urls}
                onChange={handleChange}
                input={<OutlinedInput label="URLs" />}
                renderValue={(selected) => getNames(selected, urls, 'category_Name')}
              >
                {urls.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    <Checkbox checked={formData.enabled_urls.includes(u._id)} />
                    {/* show category_Name instead of parentDomain */}
                    <ListItemText primary={u.category_Name || u.category_name || '—'} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleClose} sx={{ color: '#2d9cdb' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: '#2d9cdb', '&:hover': { backgroundColor: '#0b1d51' } }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the group{' '}
            <strong>{deletingGroup ? deletingGroup.name : ''}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{
              backgroundColor: '#e53935',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
